// Sojaolie-IBC: echte litervoorraad met FIFO-afboeking.
// Volledig geïsoleerd: leest ontvangsten (uit lotConfig.sojaolieIbc) + productie-oliekosten (€)
// en rekent die FIFO terug naar liters via de inkoopprijs per liter (oudste IBC eerst).
// Raakt GEEN afboeklogica, voorraadmotor of beveiliging aan.
const N = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const clean = (v) => (Math.abs(v) < 1e-9 ? 0 : Number(v.toFixed(4)));

// Niet-destructieve migratie: oude ontvangsten ({datum, batchnr, prijs} = €/IBC) worden
// met de bekende liters-per-IBC omgerekend naar het nieuwe formaat. De opgeslagen data
// blijft onaangeroerd; dit is puur een interpretatie bij het lezen.
export function normaliseerOntvangsten(ontvangen = [], litersPerIbc = 900) {
  return (Array.isArray(ontvangen) ? ontvangen : []).map((b) => {
    if (b && (b.liters != null || b.prijsPerLiter != null)) {
      const liters = N(b.liters);
      const prijsPerLiter = b.prijsPerLiter != null && b.prijsPerLiter !== ""
        ? N(b.prijsPerLiter)
        : (liters > 0 ? N(b.totaalPrijs) / liters : 0);
      return { ...b, liters, prijsPerLiter, tracecode: String(b.tracecode || b.batchnr || "").trim() };
    }
    // oud formaat
    const liters = N(litersPerIbc) || 900;
    const prijsPerLiter = liters > 0 ? N(b?.prijs) / liters : 0;
    return { ...b, liters, prijsPerLiter, tracecode: String(b?.batchnr || b?.tracecode || "").trim(), _gemigreerd: true };
  });
}

// ontvangen: [{id, datum, tracecode, liters, prijsPerLiter}]
// consumption: [{datum, euro}]  productieregels met oliekosten
// correcties: [{id, maand, liters, reden, door, ts}]  liters terug op voorraad (+ = minder verbruikt)
export function sojaolieLedger(ontvangen = [], consumption = [], correcties = []) {
  const lots = normaliseerOntvangsten(ontvangen)
    .filter((b) => N(b.liters) > 0 && N(b.prijsPerLiter) > 0)
    .sort((a, b) => String(a.datum || "").localeCompare(String(b.datum || "")) || String(a.id || "").localeCompare(String(b.id || "")))
    .map((b) => ({ ...b, liters: N(b.liters), prijsPerLiter: N(b.prijsPerLiter), restLiters: N(b.liters), verbruiktLiters: 0 }));

  const events = (Array.isArray(consumption) ? consumption : [])
    .filter((c) => N(c.euro) > 0)
    .sort((a, b) => String(a.datum || "").localeCompare(String(b.datum || "")));

  const fifoTrace = []; // {datum, tracecode, liters, euro, lotId}
  let i = 0;
  events.forEach((ev) => {
    let euroLeft = N(ev.euro);
    while (euroLeft > 1e-9 && i < lots.length) {
      const lot = lots[i];
      if (lot.restLiters <= 1e-9) { i++; continue; }
      const maxLiters = euroLeft / lot.prijsPerLiter;
      const take = Math.min(maxLiters, lot.restLiters);
      lot.restLiters -= take;
      lot.verbruiktLiters += take;
      euroLeft -= take * lot.prijsPerLiter;
      fifoTrace.push({ datum: ev.datum, tracecode: lot.tracecode, liters: take, euro: take * lot.prijsPerLiter, lotId: lot.id });
      if (lot.restLiters <= 1e-9) i++;
    }
    // euroLeft > 0 zonder voorraad → niet afboeken (geen negatieve voorraad)
  });

  // Handmatige maandcorrectie: liters terug op voorraad (minder verbruikt) → teruggeven aan
  // de laatst-verbruikte lots (LIFO), zodat rest én waarde consistent blijven.
  const correctieLiters = (Array.isArray(correcties) ? correcties : []).reduce((s, c) => s + N(c.liters), 0);
  let corrBack = correctieLiters;
  for (let j = lots.length - 1; j >= 0 && Math.abs(corrBack) > 1e-9; j--) {
    const lot = lots[j];
    if (corrBack > 0) {
      const back = Math.min(corrBack, lot.verbruiktLiters);
      lot.restLiters += back; lot.verbruiktLiters -= back; corrBack -= back;
    } else {
      const extra = Math.min(-corrBack, lot.restLiters);
      lot.restLiters -= extra; lot.verbruiktLiters += extra; corrBack += extra;
    }
  }

  const resLots = lots.map((l) => ({
    ...l,
    restLiters: clean(l.restLiters),
    verbruiktLiters: clean(l.verbruiktLiters),
    restWaarde: clean(l.restLiters * l.prijsPerLiter),
    pctLeeg: l.liters > 0 ? clean(1 - l.restLiters / l.liters) : 0,
  }));

  return {
    lots: resLots,
    totaalOntvangen: clean(lots.reduce((s, l) => s + l.liters, 0)),
    totaalRest: clean(resLots.reduce((s, l) => s + l.restLiters, 0)),
    totaalWaarde: clean(resLots.reduce((s, l) => s + l.restWaarde, 0)),
    verbruiktLiters: clean(resLots.reduce((s, l) => s + l.verbruiktLiters, 0)),
    berekendVerbruiktEuro: clean(events.reduce((s, e) => s + N(e.euro), 0)),
    correctieLiters: clean(correctieLiters),
    fifoTrace,
  };
}

// Olie-tracecodes (FIFO) die verbruikt zijn binnen een datumbereik — voor het recall-verslag.
export function olieTraceInPeriode(ledger, vanDatum = "", totDatum = "") {
  const map = new Map();
  (ledger?.fifoTrace || []).forEach((t) => {
    const d = String(t.datum || "");
    if (vanDatum && d < vanDatum) return;
    if (totDatum && d > totDatum) return;
    const key = t.tracecode || "(geen tracecode)";
    map.set(key, { tracecode: key, liters: (map.get(key)?.liters || 0) + N(t.liters), vanaf: !map.get(key) || d < map.get(key).vanaf ? d : map.get(key).vanaf, tot: !map.get(key) || d > map.get(key).tot ? d : map.get(key).tot });
  });
  return [...map.values()].map((x) => ({ ...x, liters: clean(x.liters) })).sort((a, b) => String(a.vanaf).localeCompare(String(b.vanaf)));
}
